import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useCart } from '../CartContext';
import { ShoppingCart, Clock, Utensils, Pizza, Sandwich, Coffee, IceCreamCone, Salad, Soup, Beef, Cookie, CupSoda, Wine } from 'lucide-react';
import ProductDetail from './ProductDetail';
import './Menu.css';

function formatPrice(price) {
  return `£${Number(price || 0).toFixed(2)}`;
}

const FOOD_ICONS = [Utensils, Pizza, Sandwich, Coffee, IceCreamCone, Salad, Soup, Beef, Cookie, CupSoda];
function PlaceholderIcon({ id, size = 28 }) {
  const idx = (id || 0) % FOOD_ICONS.length;
  const Icon = FOOD_ICONS[idx];
  return <Icon size={size} strokeWidth={1.5} />;
}

function getDietaryTags(variation) {
  if (!variation?.dietary_tags) return [];
  const dt = variation.dietary_tags;
  const tags = [];
  if (typeof dt === 'object' && !Array.isArray(dt)) {
    if (dt.vegan) tags.push('VG');
    if (dt.vegetarian && !dt.vegan) tags.push('V');
    if (dt.gluten_free) tags.push('GF');
  } else if (Array.isArray(dt)) {
    return dt;
  }
  return tags;
}

// Build display items from the menu config + products data
function buildMenuItems(menuConfig, products) {
  if (!menuConfig?.categories?.length || !products?.length) {
    // Fallback: group all products by reporting_category
    return buildFallbackMenu(products);
  }

  const productMap = {};
  for (const p of products) {
    productMap[String(p.id)] = p;
  }

  const sections = [];

  for (const cat of menuConfig.categories) {
    const items = [];
    for (const entry of (cat.products || [])) {
      const product = productMap[String(entry.product_id)];
      if (!product) continue;

      if (entry.variation_id != null) {
        // Specific variation (loose comparison to handle string/number mismatch from JSON config)
        const variation = (product.variations || []).find(v => String(v.id) === String(entry.variation_id));
        if (!variation) continue;
        items.push({
          key: `${entry.product_id}-${entry.variation_id}`,
          product,
          variation,
          displayName: variation.name || product.name,
          displayPrice: variation.price ?? product.price ?? 0,
          imageUrl: variation.online_image_url || variation.image_url || product.online_image_url || product.image_url,
          featured: !!entry.featured,
          dietaryTags: getDietaryTags(variation),
          isActive: product.is_active !== false && variation.is_active !== false,
          outOfStock: !!product.out_of_stock || !!variation.out_of_stock
        });
      } else {
        // Product without variations
        items.push({
          key: `${entry.product_id}`,
          product,
          variation: null,
          displayName: product.name,
          displayPrice: product.price ?? 0,
          imageUrl: product.online_image_url || product.image_url,
          featured: !!entry.featured,
          dietaryTags: [],
          isActive: product.is_active !== false,
          outOfStock: !!product.out_of_stock
        });
      }
    }

    if (items.length > 0) {
      sections.push({
        id: cat.id,
        name: cat.name,
        description: cat.description || null,
        items
      });
    }
  }

  return sections;
}

function buildFallbackMenu(products) {
  if (!products?.length) return [];
  const catMap = {};
  for (const p of products) {
    const catName = p.reporting_category || 'Other';
    if (!catMap[catName]) {
      catMap[catName] = { id: catName, name: catName, items: [] };
    }

    const variations = p.variations || [];
    if (variations.length > 0) {
      for (const v of variations) {
        catMap[catName].items.push({
          key: `${p.id}-${v.id}`,
          product: p,
          variation: v,
          displayName: v.name || p.name,
          displayPrice: v.price ?? p.price ?? 0,
          imageUrl: v.online_image_url || v.image_url || p.online_image_url || p.image_url,
          featured: false,
          dietaryTags: getDietaryTags(v),
          isActive: p.is_active !== false && v.is_active !== false,
          outOfStock: !!p.out_of_stock || !!v.out_of_stock
        });
      }
    } else {
      catMap[catName].items.push({
        key: `${p.id}`,
        product: p,
        variation: null,
        displayName: p.name,
        displayPrice: p.price ?? 0,
        imageUrl: p.online_image_url || p.image_url,
        featured: false,
        dietaryTags: [],
        isActive: p.is_active !== false,
        outOfStock: !!p.out_of_stock
      });
    }
  }
  return Object.values(catMap);
}

function ProductCard({ item, disabled, onSelect }) {
  const itemInactive = item.isActive === false;
  const itemOutOfStock = !!item.outOfStock;
  const isDisabled = disabled || itemInactive || itemOutOfStock;

  const handleClick = () => {
    if (isDisabled) return;
    onSelect(item);
  };

  return (
    <div
      className={`product-card${item.featured ? ' product-card--featured' : ''}${isDisabled ? ' product-card--disabled' : ''}${itemOutOfStock ? ' product-card--out-of-stock' : ''}`}
      onClick={handleClick}
      role="button"
      tabIndex={isDisabled ? -1 : 0}
      onKeyDown={e => e.key === 'Enter' && handleClick()}
      aria-label={`${item.displayName}, ${formatPrice(item.displayPrice)}${itemOutOfStock ? ', out of stock' : itemInactive ? ', unavailable' : ''}`}
    >
      <div className="product-card__image-wrap">
        {item.imageUrl ? (
          <img
            src={item.imageUrl}
            alt={item.displayName}
            className="product-card__image"
            loading="lazy"
          />
        ) : (
          <div className="product-card__image-placeholder">
            <PlaceholderIcon id={item.product.id} />
          </div>
        )}
        {itemInactive && <span className="product-card__unavailable-badge">Unavailable</span>}
        {!itemInactive && item.featured && <span className="product-card__popular-badge">Popular</span>}
        {itemOutOfStock && (
          <div className="product-card__out-of-stock-badge">Out of Stock</div>
        )}
      </div>
      <div className="product-card__body">
        <p className="product-card__name">{item.displayName}</p>
        <p className="product-card__price">{formatPrice(item.displayPrice)}</p>
        {item.dietaryTags.length > 0 && (
          <div className="product-card__tags">
            {item.dietaryTags.map(tag => (
              <span key={tag} className={`dietary-tag dietary-tag--${tag.toLowerCase()}`}>{tag}</span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default function Menu({ products, menuConfig, disabled, waitTime, onViewCart }) {
  const { itemCount, total } = useCart();
  const [activeCategory, setActiveCategory] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [closedMessage, setClosedMessage] = useState(false);
  const sectionRefs = useRef({});
  const tabsRef = useRef(null);

  const sections = useMemo(() => buildMenuItems(menuConfig, products), [menuConfig, products]);

  useEffect(() => {
    if (sections.length > 0 && activeCategory === null) {
      setActiveCategory(sections[0].id);
    }
  }, [sections, activeCategory]);

  const scrollToCategory = useCallback((sectionId) => {
    setActiveCategory(sectionId);
    const el = sectionRefs.current[sectionId];
    if (el) {
      const tabsHeight = tabsRef.current ? tabsRef.current.offsetHeight : 56;
      const headerEl = document.querySelector('.app-header');
      const bannerEl = document.querySelector('.kitchen-closed-banner');
      let offset = tabsHeight;
      if (headerEl) offset += headerEl.offsetHeight;
      if (bannerEl) offset += bannerEl.offsetHeight;
      const top = el.getBoundingClientRect().top + window.scrollY - offset - 8;
      window.scrollTo({ top, behavior: 'smooth' });
    }

    const tabBar = tabsRef.current;
    if (tabBar) {
      const activeTab = tabBar.querySelector('.category-tab--active');
      if (activeTab) {
        activeTab.scrollIntoView({ inline: 'center', block: 'nearest', behavior: 'smooth' });
      }
    }
  }, []);

  // Update active tab on scroll
  useEffect(() => {
    const handleScroll = () => {
      const tabsHeight = tabsRef.current ? tabsRef.current.offsetHeight : 56;
      const headerEl = document.querySelector('.app-header');
      const bannerEl = document.querySelector('.kitchen-closed-banner');
      let offset = tabsHeight + 16;
      if (headerEl) offset += headerEl.offsetHeight;
      if (bannerEl) offset += bannerEl.offsetHeight;

      for (let i = sections.length - 1; i >= 0; i--) {
        const section = sections[i];
        const el = sectionRefs.current[section.id];
        if (el) {
          const top = el.getBoundingClientRect().top;
          if (top <= offset) {
            setActiveCategory(section.id);
            break;
          }
        }
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [sections]);

  const handleProductSelect = (item) => {
    if (disabled) {
      setClosedMessage(true);
      setTimeout(() => setClosedMessage(false), 2500);
      return;
    }
    setSelectedItem(item);
  };

  return (
    <div className="menu">
      {/* Category tabs */}
      {sections.length > 1 && (
        <div className="category-tabs" ref={tabsRef}>
          <div className="category-tabs__inner">
            {sections.map(section => (
              <button
                key={section.id}
                className={`category-tab${activeCategory === section.id ? ' category-tab--active' : ''}`}
                onClick={() => scrollToCategory(section.id)}
              >
                {section.name}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Kitchen closed toast */}
      {closedMessage && (
        <div className="kitchen-closed-toast">Kitchen is closed — ordering unavailable</div>
      )}

      {/* Product sections */}
      <div className="menu__content">
        {sections.map(section => (
          <section
            key={section.id}
            className="menu-section"
            ref={el => { sectionRefs.current[section.id] = el; }}
          >
            <h2 className="menu-section__title">{section.name}</h2>
            {section.description && (
              <p className="menu-section__description">{section.description}</p>
            )}
            <div className="product-grid">
              {section.items.map(item => (
                <ProductCard
                  key={item.key}
                  item={item}
                  disabled={disabled}
                  onSelect={handleProductSelect}
                />
              ))}
            </div>
          </section>
        ))}
      </div>

      {/* No items message */}
      {sections.length === 0 && (
        <div className="menu__empty">
          <p>No items available right now.</p>
        </div>
      )}

      {/* Wait time indicator */}
      {waitTime >= 1 && (
        <div className="wait-time-pill" aria-label={`Estimated wait: ${waitTime} mins`}>
          <Clock size={14} />
          <span>{waitTime} min{waitTime !== 1 ? 's' : ''}</span>
        </div>
      )}

      {/* Floating cart button */}
      {itemCount > 0 && (
        <button className="cart-fab" onClick={onViewCart} aria-label={`View cart, ${itemCount} items, ${formatPrice(total)}`}>
          <span className="cart-fab__icon"><ShoppingCart size={18} /></span>
          <span className="cart-fab__total">{formatPrice(total)}</span>
          <span className="cart-fab__badge">{itemCount}</span>
        </button>
      )}

      {/* ProductDetail modal — pass product + pre-selected variation */}
      {selectedItem && (
        <ProductDetail
          product={selectedItem.product}
          preSelectedVariation={selectedItem.variation}
          onClose={() => setSelectedItem(null)}
        />
      )}
    </div>
  );
}
